import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Smartphone, Monitor, Trash2, Check, X } from "lucide-react";
import {
  getTeacherLoginDevices,
  deleteTeacherLoginDevice,
  resolveTeacherDeviceRequest,
} from "../../../api/crm/teacherAccounts/teacherAccountInfo";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { confirmDelete, DELETE_ENTITY } from "../../../utils/confirmDelete";
import { hasPermission } from "../../../utils/permissionUtils";

function shortHash(hex) {
  if (!hex || typeof hex !== "string") return "—";
  const s = hex.replace(/[^a-f0-9]/gi, "");
  if (s.length <= 10) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function deviceClassMeta(deviceClass) {
  if (deviceClass === "mobile") {
    return {
      label: "Điện thoại",
      Icon: Smartphone,
      badge: "bg-sky-50 text-sky-700 border-sky-200",
    };
  }
  return {
    label: "Máy tính",
    Icon: Monitor,
    badge: "bg-violet-50 text-violet-700 border-violet-200",
  };
}

const TeacherDevicePanel = ({ employeeId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const d = await getTeacherLoginDevices(employeeId);
      setData(d);
    } catch {
      showErrorToast("Không tải được danh sách thiết bị.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const canWrite = hasPermission("crm.teacher_accounts", "U");

  const handleDelete = async (deviceId) => {
    const c = await confirmDelete({ type: DELETE_ENTITY.teacher_device });
    if (!c.isConfirmed) return;
    setBusyId(deviceId);
    try {
      await deleteTeacherLoginDevice(employeeId, deviceId);
      showSuccessToast("Đã gỡ thiết bị.");
      await load();
    } catch (e) {
      showErrorToast(e?.response?.data?.error || "Không gỡ được.");
    } finally {
      setBusyId(null);
    }
  };

  const handleResolveRequest = async (requestId, action) => {
    setBusyId(requestId);
    try {
      await resolveTeacherDeviceRequest(requestId, action);
      showSuccessToast(action === "approve" ? "Đã duyệt thiết bị." : "Đã từ chối yêu cầu.");
      await load();
    } catch (e) {
      showErrorToast(e?.response?.data?.error || "Không xử lý được.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8 text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  const devices = data?.devices || [];
  const pending = data?.pending_requests || [];
  const slots = data?.slots || {
    desktop: devices.some((d) => d.device_class === "desktop"),
    mobile: devices.some((d) => d.device_class === "mobile"),
  };

  return (
    <div className="space-y-4 text-sm">
      <p className="text-xs text-gray-500 leading-relaxed">
        Mỗi tài khoản chỉ được đăng nhập trên{" "}
        <span className="font-semibold text-gray-700">1 máy tính</span> và{" "}
        <span className="font-semibold text-gray-700">1 điện thoại</span> (không
        phải 2 thiết bị cùng loại). Khi duyệt hoặc đăng nhập thiết bị mới cùng loại, hệ thống sẽ tự động kích hoạt và ngắt phiên (force logout) thiết bị cũ.
      </p>

      <div className="grid grid-cols-2 gap-2">
        {[
          { key: "desktop", label: "Máy tính", Icon: Monitor },
          { key: "mobile", label: "Điện thoại", Icon: Smartphone },
        ].map(({ key, label, Icon }) => {
          const used = !!slots[key];
          return (
            <div
              key={key}
              className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                used
                  ? "border-emerald-200 bg-emerald-50/70 text-emerald-800"
                  : "border-gray-200 bg-white text-gray-500"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-wide">{label}</p>
                <p className="text-[10px]">{used ? "Đã đăng ký" : "Còn trống"}</p>
              </div>
            </div>
          );
        })}
      </div>

      {pending.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 space-y-2">
          <p className="text-[11px] font-bold uppercase text-amber-900">Yêu cầu thiết bị chờ duyệt</p>
          <ul className="space-y-2">
            {pending.map((r) => {
              const meta = deviceClassMeta(r.device_class);
              const Icon = meta.Icon;
              return (
                <li
                  key={r.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/90 px-3 py-2 border border-amber-100"
                >
                  <div className="min-w-0">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${meta.badge}`}
                      >
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                      <span className="font-mono text-xs text-gray-800">
                        {shortHash(r.fingerprint_hash)}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 truncate max-w-[280px]">
                      {r.user_agent || "—"}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {r.created_at ? new Date(r.created_at).toLocaleString("vi-VN") : ""}
                    </p>
                  </div>
                  {canWrite && (
                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        disabled={busyId === r.id}
                        onClick={() => handleResolveRequest(r.id, "approve")}
                        className="p-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                        title="Duyệt"
                      >
                        {busyId === r.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check size={16} />
                        )}
                      </button>
                      <button
                        type="button"
                        disabled={busyId === r.id}
                        onClick={() => handleResolveRequest(r.id, "reject")}
                        className="p-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        title="Từ chối"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="w-full text-left text-xs min-w-[560px]">
          <thead className="bg-gray-50 text-gray-500 uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2">Loại</th>
              <th className="px-3 py-2">Mã thiết bị (hash)</th>
              <th className="px-3 py-2">Lần cuối</th>
              <th className="px-3 py-2 w-24 text-right">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {devices.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-gray-500 italic">
                  Chưa có thiết bị đăng ký (lần đăng nhập đầu sẽ tự ghi nhận).
                </td>
              </tr>
            ) : (
              devices.map((d) => {
                const meta = deviceClassMeta(d.device_class);
                const Icon = meta.Icon;
                return (
                  <tr key={d.id} className="border-t border-gray-100 hover:bg-gray-50/80">
                    <td className="px-3 py-2 align-top">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${meta.badge}`}
                      >
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <p className="font-mono text-gray-800">{shortHash(d.fingerprint_hash)}</p>
                      <p className="text-[11px] text-gray-500 line-clamp-2">{d.user_agent || "—"}</p>
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {d.last_seen_at
                        ? new Date(d.last_seen_at).toLocaleString("vi-VN")
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canWrite ? (
                        <button
                          type="button"
                          disabled={busyId === d.id}
                          onClick={() => handleDelete(d.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 text-[11px] font-bold uppercase disabled:opacity-50"
                        >
                          {busyId === d.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Trash2 size={14} />
                          )}
                          Gỡ
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TeacherDevicePanel;
